import React from "react";
import {
  Box,
  Checkbox,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Typography,
} from "@mui/material";

export const LIST_PAGE_SIZE_OPTIONS = [10, 15, 20, 50, 100] as const;
export const LIST_PAGE_SIZE_ALL = "all" as const;

export type ListSortOption = {
  id: string;
  label: string;
};

export function ListToolbarPagination({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  sortColumn,
  sortDirection,
  sortOptions,
  onSortChange,
  selectable,
  allSelected,
  someSelected,
  onToggleSelectAll,
}: {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number | typeof LIST_PAGE_SIZE_ALL) => void;
  sortColumn?: string;
  sortDirection?: 1 | -1;
  sortOptions?: ListSortOption[];
  onSortChange?: (sortColumn: string, sortDirection: 1 | -1) => void;
  selectable?: boolean;
  allSelected?: boolean;
  someSelected?: boolean;
  onToggleSelectAll?: (checked: boolean) => void;
}) {
  const pageCount = Math.max(1, Math.ceil((total || 0) / Math.max(pageSize, 1)));
  const isAll = pageSize >= 10000;
  const sizeValue = isAll ? LIST_PAGE_SIZE_ALL : pageSize;
  const controlSx = {
    minWidth: { xs: 0, sm: 110 },
    width: { xs: "100%", sm: "auto" },
    flex: { xs: "1 1 140px", sm: "0 0 auto" },
  };

  return (
    <Stack
      direction="column"
      spacing={1.5}
      sx={{
        px: { xs: 1.5, sm: 2 },
        py: 1.5,
        borderTop: "1px solid #e5e7eb",
        bgcolor: "#fafafa",
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
        sx={{ width: "100%" }}
      >
        {selectable && (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Checkbox
              size="small"
              checked={Boolean(allSelected)}
              indeterminate={Boolean(someSelected) && !allSelected}
              onChange={(e) => onToggleSelectAll?.(e.target.checked)}
              inputProps={{ "aria-label": "Select all rows on page" }}
            />
            <Typography variant="caption" color="text.secondary">
              Select page
            </Typography>
          </Stack>
        )}
        <Typography variant="body2" color="text.secondary">
          {total} total
        </Typography>
        <FormControl size="small" sx={controlSx}>
          <InputLabel id="rows-per-page-label">Rows</InputLabel>
          <Select
            labelId="rows-per-page-label"
            label="Rows"
            value={sizeValue}
            onChange={(e) => {
              const v = e.target.value;
              if (v === LIST_PAGE_SIZE_ALL) onPageSizeChange(LIST_PAGE_SIZE_ALL);
              else onPageSizeChange(Number(v));
            }}
          >
            {LIST_PAGE_SIZE_OPTIONS.map((n) => (
              <MenuItem key={n} value={n}>
                {n}
              </MenuItem>
            ))}
            <MenuItem value={LIST_PAGE_SIZE_ALL}>All</MenuItem>
          </Select>
        </FormControl>
        {sortOptions && sortOptions.length > 0 && onSortChange && (
          <>
            <FormControl size="small" sx={{ ...controlSx, minWidth: { xs: 0, sm: 140 } }}>
              <InputLabel id="sort-column-label">Sort by</InputLabel>
              <Select
                labelId="sort-column-label"
                label="Sort by"
                value={sortColumn || sortOptions[0].id}
                onChange={(e) =>
                  onSortChange(String(e.target.value), sortDirection === 1 ? 1 : -1)
                }
              >
                {sortOptions.map((opt) => (
                  <MenuItem key={opt.id} value={opt.id}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={controlSx}>
              <InputLabel id="sort-dir-label">Order</InputLabel>
              <Select
                labelId="sort-dir-label"
                label="Order"
                value={sortDirection === 1 ? "asc" : "desc"}
                onChange={(e) =>
                  onSortChange(
                    sortColumn || sortOptions[0].id,
                    e.target.value === "asc" ? 1 : -1,
                  )
                }
              >
                <MenuItem value="asc">Ascending</MenuItem>
                <MenuItem value="desc">Descending</MenuItem>
              </Select>
            </FormControl>
          </>
        )}
      </Stack>
      <Box sx={{ display: "flex", justifyContent: "center", width: "100%", overflowX: "auto" }}>
        <Pagination
          color="primary"
          size="small"
          page={Math.min(page, pageCount)}
          count={pageCount}
          onChange={(_e, next) => onPageChange(next)}
          siblingCount={0}
          boundaryCount={1}
        />
      </Box>
    </Stack>
  );
}
